import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { FORM_ENDPOINT, CONTACT_EMAIL } from "../config";
import {
  SERVICE_OPTIONS, BUDGET_OPTIONS, TIMELINE_OPTIONS, CONTACT_METHODS,
} from "../data/content";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const FIELD = "w-full rounded-xl border border-white/15 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40 transition-colors";

const EMPTY_FORM = {
  name: "", company: "", email: "", phone: "", website: "",
  business_type: "", budget: "", timeline: "", description: "",
  preferred_contact: "Email", consent: false,
};

export default function Contact() {
  const location = useLocation();
  const preselect = location.state?.service;
  const [status, setStatus] = useState("idle"); // idle | success | error
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [services, setServices] = useState(
    preselect ? [preselect.includes("Signature") ? "Custom / Signature Project" : "Website Development"] : []
  );
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.consent) e.consent = "Please provide consent to continue.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (loading) return; // prevent duplicate submissions while processing
    setStatus("idle");
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      if (!FORM_ENDPOINT) throw new Error("Form endpoint is not configured.");
      const payload = {
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        website: form.website,
        business_type: form.business_type,
        services: services.join(", "),
        budget: form.budget,
        timeline: form.timeline,
        description: form.description,
        preferred_contact: form.preferred_contact,
        consent: form.consent ? "Yes" : "No",
        _subject: `New Nova SecureNet inquiry from ${form.name}`,
      };
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setStatus("success");
        setForm(EMPTY_FORM);
        setServices([]);
        setErrors({});
        toast.success("Inquiry sent successfully!");
      } else {
        setStatus("error");
        toast.error("Your inquiry could not be sent.");
      }
    } catch (err) {
      setStatus("error");
      toast.error("Your inquiry could not be sent.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Get in touch"
        title="Let's start a conversation"
        sub="Tell us about your project and we'll get back to you within 24–48 hours with clear next steps."
      />

      <section className="mx-auto max-w-6xl px-5 pb-24 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          {/* Info side */}
          <div className="space-y-4">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
              <h3 className="font-heading text-lg font-semibold text-white">Reach us directly</h3>
              <ul className="mt-6 space-y-5">
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-gradient"><Mail className="h-5 w-5 text-white" /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/40">Email</p>
                    <a href="mailto:matthew@novasecurenet.com" className="text-sm text-white hover:text-brand-gold">matthew@novasecurenet.com</a>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/5"><Phone className="h-5 w-5 text-brand" /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/40">Response time</p>
                    <p className="text-sm text-white">Within 24–48 hours</p>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/5"><MapPin className="h-5 w-5 text-brand" /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/40">Based in</p>
                    <p className="text-sm text-white">Canada · Remote-first</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="rounded-3xl border border-white/10 bg-ink-soft p-8">
              <p className="text-sm leading-relaxed text-muted-foreground">
                Prefer to talk it through? Book a free, no-pressure consultation and we'll help you
                figure out the right path forward.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 lg:p-10">
            <motion.form
              onSubmit={submit}
              data-testid="contact-form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
              noValidate
            >
              {status === "success" && (
                <motion.div
                  data-testid="contact-success"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3 rounded-2xl border border-brand/40 bg-brand/10 p-4"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  <p className="text-sm text-white">
                    Thank you. Your inquiry has been sent successfully. We'll be in touch shortly.
                  </p>
                </motion.div>
              )}
              {status === "error" && (
                <motion.div
                  data-testid="contact-error"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 p-4"
                >
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
                  <p className="text-sm text-white">
                    Your inquiry could not be sent. Please try again or email{" "}
                    <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-error-mailto" className="font-medium text-brand-gold underline underline-offset-4 hover:text-white">
                      {CONTACT_EMAIL}
                    </a>.
                  </p>
                </motion.div>
              )}

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <Label className="mb-2 block text-sm text-white/80">Name *</Label>
                      <input data-testid="input-name" className={FIELD} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" />
                      {errors.name && <p className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm text-white/80">Company Name</Label>
                      <input data-testid="input-company" className={FIELD} value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Your business" />
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm text-white/80">Email *</Label>
                      <input data-testid="input-email" type="email" className={FIELD} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" />
                      {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm text-white/80">Phone Number</Label>
                      <input data-testid="input-phone" className={FIELD} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(000) 000-0000" />
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm text-white/80">Website (optional)</Label>
                      <input data-testid="input-website" className={FIELD} value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" />
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm text-white/80">Type of Business</Label>
                      <input data-testid="input-business-type" className={FIELD} value={form.business_type} onChange={(e) => set("business_type", e.target.value)} placeholder="e.g. Restaurant, Clinic" />
                    </div>
                  </div>

                  <div>
                    <Label className="mb-3 block text-sm text-white/80">Services Interested In</Label>
                    <div className="grid gap-2.5 sm:grid-cols-2">
                      {SERVICE_OPTIONS.map((s) => (
                        <label
                          key={s}
                          data-testid={`service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/,"")}`}
                          className={["flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors", services.includes(s) ? "border-brand/50 bg-brand/10 text-white" : "border-white/10 bg-white/[0.02] text-muted-foreground hover:border-white/25"].join(" ")}
                        >
                          <Checkbox checked={services.includes(s)} onCheckedChange={() => toggleService(s)} className="border-white/30 data-[state=checked]:bg-brand data-[state=checked]:border-brand" />
                          {s}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <Label className="mb-2 block text-sm text-white/80">Estimated Budget</Label>
                      <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                        <SelectTrigger data-testid="select-budget" className={FIELD}><SelectValue placeholder="Select a range" /></SelectTrigger>
                        <SelectContent>
                          {BUDGET_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm text-white/80">Desired Timeline</Label>
                      <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
                        <SelectTrigger data-testid="select-timeline" className={FIELD}><SelectValue placeholder="Select a timeline" /></SelectTrigger>
                        <SelectContent>
                          {TIMELINE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block text-sm text-white/80">Project Description</Label>
                    <Textarea data-testid="input-description" rows={4} className={FIELD} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Tell us a bit about your project, goals, and anything else we should know." />
                  </div>

                  <div>
                    <Label className="mb-2 block text-sm text-white/80">Preferred Contact Method</Label>
                    <div className="flex gap-2">
                      {CONTACT_METHODS.map((m) => (
                        <button
                          type="button"
                          key={m}
                          data-testid={`contact-method-${m.toLowerCase()}`}
                          onClick={() => set("preferred_contact", m)}
                          className={["flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition-colors", form.preferred_contact === m ? "border-brand/50 bg-brand/10 text-white" : "border-white/10 bg-white/[0.02] text-muted-foreground hover:border-white/25"].join(" ")}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="flex cursor-pointer items-start gap-3" data-testid="consent-wrapper">
                    <Checkbox data-testid="input-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-brand data-[state=checked]:border-brand" />
                    <span className="text-sm text-muted-foreground">
                      I consent to Nova SecureNet contacting me about my inquiry and storing my details for this purpose. *
                    </span>
                  </label>
                  {errors.consent && <p className="-mt-3 text-xs text-red-400">{errors.consent}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    aria-busy={loading}
                    data-testid="contact-submit"
                    className="group relative inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-gradient px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_40px_-12px_rgba(255,90,54,0.7)] transition-transform duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-ink disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                  >
                    {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Send My Inquiry"}
                  </button>
                </motion.form>
          </div>
        </div>
      </section>
    </div>
  );
}
